import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Check, AlertCircle, Loader2, Trash2, DollarSign, Sparkles, MapPin, ChevronRight } from 'lucide-react';
import { useContractForm } from '../hooks/useContractForm';
import { customChargeTemplatesAPI } from '../services/api';
import { CustomMenuTiers } from '../components/CustomMenuTiers';
import AIAssistModal from '../components/AIAssistModal';
import {
  ContractCard, EventTimingSection, SetupRequirementsSection, AddonsSection,
  BarServiceSection, TravelFeeSection, TablePlannerSection, RentalsSection,
  EquipmentSection, ClientAccessModal, EventCaptainBrief, DietarySection
} from '../components/contracts';
import { invoicesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Contracts = () => {
  const navigate = useNavigate();
  const cf = useContractForm();
  const { toast } = useToast();

  const viewBEO = (c) => navigate('/beo', { state: { contract: c } });
  const viewContractPreview = (c) => navigate('/contract-preview', { state: { contract: c } });
  const viewMenuPreview = (c) => navigate('/menu-preview', { state: { contract: c } });

  // AI Email Draft modal state
  const [aiEmailContract, setAiEmailContract] = React.useState(null);

  // Convert contract → invoice in one click. If an invoice already exists for
  // this contract, jump straight to the Invoices page so the user can open it.
  const convertContractToInvoice = async (contract) => {
    try {
      const existing = await invoicesAPI.getAll().catch(() => []);
      const already = (existing || []).find(inv => inv.contract_id === contract.id);
      if (already) {
        toast({ title: `Invoice ${already.invoice_number} already exists`, description: 'Opening Invoices page…' });
        navigate('/invoices');
        return;
      }
      const subtotal = parseFloat(contract.subtotal) || 0;
      // Tax 13% on subtotal; fall back to stored value if present
      const tax_amount = parseFloat(contract.tax_amount) > 0
        ? parseFloat(contract.tax_amount)
        : +(subtotal * 0.13).toFixed(2);
      const gratuity_amount = parseFloat(contract.gratuity_amount) || 0;
      const total_amount = parseFloat(contract.total_amount) > 0
        ? parseFloat(contract.total_amount)
        : +(subtotal + tax_amount + gratuity_amount).toFixed(2);
      const created = await invoicesAPI.create({
        contract_id: contract.id,
        contract_number: contract.contract_number,
        client_name: contract.client_name,
        client_email: contract.client_email,
        client_phone: contract.client_phone,
        event_name: contract.event_name,
        event_date: contract.event_date,
        subtotal,
        tax_amount,
        gratuity_amount,
        total_amount,
      });
      toast({ title: `Invoice ${created.invoice_number || ''} created`, description: 'Opening Invoices page…' });
      navigate('/invoices');
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Could not create invoice', variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">Contracts</h1>
          <p className="text-sm text-gray-500 mt-1">Manage event contracts and proposals</p>
        </div>
        <Dialog open={cf.isDialogOpen} onOpenChange={(open) => { cf.setIsDialogOpen(open); if (!open) cf.resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-contract-btn" className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white">
              <Plus size={20} className="mr-2" /> New Contract
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{cf.editingContract ? 'Edit Contract' : 'Create New Contract'}</DialogTitle>
                {!cf.editingContract && cf.packageTemplates.length > 0 && (
                  <Select onValueChange={(v) => cf.applyTemplate(v)}>
                    <SelectTrigger className="w-52 h-8 text-xs" data-testid="template-select"><SelectValue placeholder="Use Template..." /></SelectTrigger>
                    <SelectContent>
                      {cf.packageTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </DialogHeader>
            <form onSubmit={cf.handleSubmit} className="space-y-4">
              {/* Client Information */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-[#1e3a5f] mb-3">Client Information</h3>
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <Label className="text-sm text-gray-600 mb-2 block">Select Existing Client (Optional)</Label>
                  <Select onValueChange={(clientId) => {
                    const client = cf.clients.find(c => c.id === clientId);
                    if (client) cf.setFormData({ ...cf.formData, clientId: client.id, clientName: client.name, clientEmail: client.email || '', clientPhone: client.phone || '' });
                  }}>
                    <SelectTrigger data-testid="select-existing-client"><SelectValue placeholder="Choose a client to auto-fill..." /></SelectTrigger>
                    <SelectContent>{cf.clients.map(client => (<SelectItem key={client.id} value={client.id}>{client.name} {client.email ? `- ${client.email}` : ''}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="clientName">Client Name *</Label><Input id="clientName" data-testid="client-name-input" value={cf.formData.clientName} onChange={(e) => cf.setFormData({ ...cf.formData, clientName: e.target.value })} required /></div>
                  <div><Label htmlFor="clientEmail">Client Email *</Label><Input id="clientEmail" type="email" data-testid="client-email-input" value={cf.formData.clientEmail} onChange={(e) => cf.setFormData({ ...cf.formData, clientEmail: e.target.value })} required /></div>
                  <div><Label htmlFor="clientPhone">Client Phone *</Label><Input id="clientPhone" data-testid="client-phone-input" value={cf.formData.clientPhone} onChange={(e) => cf.setFormData({ ...cf.formData, clientPhone: e.target.value })} required /></div>
                  <div><Label htmlFor="eventName">Event Name *</Label><Input id="eventName" data-testid="event-name-input" value={cf.formData.eventName} onChange={(e) => cf.setFormData({ ...cf.formData, eventName: e.target.value })} required /></div>
                </div>
                <div className="mt-4"><Label htmlFor="billingAddress">Billing Address</Label><Input id="billingAddress" data-testid="billing-address-input" value={cf.formData.billingAddress} onChange={(e) => cf.setFormData({ ...cf.formData, billingAddress: e.target.value })} placeholder="Street, City, Province, Postal Code" /></div>
                <div className="mt-4 pt-3 border-t border-dashed">
                  <Label className="text-sm font-medium text-[#1e3a5f] mb-2 block">Secondary Client (Optional - for weddings)</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><Label htmlFor="secondaryClientName" className="text-xs text-gray-500">Name</Label><Input id="secondaryClientName" data-testid="secondary-client-name" value={cf.formData.secondaryClientName || ''} onChange={(e) => cf.setFormData({ ...cf.formData, secondaryClientName: e.target.value })} placeholder="e.g., Groom's family" /></div>
                    <div><Label htmlFor="secondaryClientEmail" className="text-xs text-gray-500">Email</Label><Input id="secondaryClientEmail" type="email" data-testid="secondary-client-email" value={cf.formData.secondaryClientEmail || ''} onChange={(e) => cf.setFormData({ ...cf.formData, secondaryClientEmail: e.target.value })} /></div>
                    <div><Label htmlFor="secondaryClientPhone" className="text-xs text-gray-500">Phone</Label><Input id="secondaryClientPhone" data-testid="secondary-client-phone" value={cf.formData.secondaryClientPhone || ''} onChange={(e) => cf.setFormData({ ...cf.formData, secondaryClientPhone: e.target.value })} /></div>
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-[#1e3a5f] mb-3">Event Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Event Type *</Label><Select value={cf.formData.eventType} onValueChange={(v) => { cf.setFormData({ ...cf.formData, eventType: v }); cf.setSelectedMenuIds([]); if (v === 'pickup' || v === 'dropoff') cf.setShowCustomMenuSection(true); }}><SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger><SelectContent>{cf.eventTypes && cf.eventTypes.length > 0 ? cf.eventTypes.map(t => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>) : (<><SelectItem value="onsite">Onsite</SelectItem><SelectItem value="offsite">Offsite</SelectItem><SelectItem value="tournament">Tournament</SelectItem><SelectItem value="cocktail_coach">Cocktail Coach</SelectItem><SelectItem value="pickup">Catering Pick Up</SelectItem><SelectItem value="dropoff">Catering Drop Off</SelectItem><SelectItem value="celebration_of_life">Celebration of Life</SelectItem><SelectItem value="bridal_shower">Bridal Shower</SelectItem><SelectItem value="baby_shower">Baby Shower</SelectItem><SelectItem value="birthday">Birthday</SelectItem><SelectItem value="rehearsal_dinner">Rehearsal Dinner</SelectItem><SelectItem value="golf_tournament">Golf Tournament</SelectItem><SelectItem value="rustic_ranch">Rustic Ranch</SelectItem></>)}</SelectContent></Select></div>
                  <div><Label>Location Type *</Label><Select value={cf.formData.locationType} onValueChange={(v) => cf.setFormData({ ...cf.formData, locationType: v })}><SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="onsite">On-site</SelectItem><SelectItem value="offsite">Off-site</SelectItem></SelectContent></Select></div>
                  <div>
                    <Label>Hors d&rsquo;oeuvres Service</Label>
                    <Select value={cf.formData.horsDoeuvresService || 'none'} onValueChange={(v) => cf.setFormData({ ...cf.formData, horsDoeuvresService: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="hors-doeuvres-service-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="stationed">Stationed</SelectItem>
                        <SelectItem value="passed">Passed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Chafing Dishes (buffet)</Label>
                    <Input type="number" min="0" step="1" value={cf.formData.chafingDishCount} onChange={(e) => cf.setFormData({ ...cf.formData, chafingDishCount: e.target.value })} placeholder="0" data-testid="chafing-dish-count-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div><Label>Event Date *</Label><Input type="date" data-testid="event-date-input" value={cf.formData.eventDate} onChange={(e) => cf.setFormData({ ...cf.formData, eventDate: e.target.value })} required /></div>
                  <div><Label>Event Time *</Label><Input type="time" data-testid="event-time-input" value={cf.formData.eventTime} onChange={(e) => cf.setFormData({ ...cf.formData, eventTime: e.target.value })} required /></div>
                  <div><Label>Guest Count *</Label><Input type="number" data-testid="guest-count-input" value={cf.formData.guestCount} onChange={(e) => cf.setFormData({ ...cf.formData, guestCount: e.target.value })} required /></div>
                </div>
                <div className="mt-4"><Label>Location Address *</Label><Input data-testid="location-input" value={cf.formData.location} onChange={(e) => cf.setFormData({ ...cf.formData, location: e.target.value })} required /></div>
                <div className="mt-4"><Label>Service Style</Label><Select value={cf.formData.serviceStyle} onValueChange={(v) => cf.setFormData({ ...cf.formData, serviceStyle: v })}><SelectTrigger data-testid="service-style-select"><SelectValue placeholder="Select service style" /></SelectTrigger><SelectContent><SelectItem value="buffet">Buffet</SelectItem><SelectItem value="plated">Plated</SelectItem><SelectItem value="breakfast_buffet">Breakfast Buffet</SelectItem><SelectItem value="lunch_buffet">Lunch Buffet</SelectItem><SelectItem value="plated_lunch">Plated Lunch</SelectItem><SelectItem value="dinner_buffet">Dinner Buffet</SelectItem><SelectItem value="family_style">Family Style</SelectItem><SelectItem value="stations">Food Stations</SelectItem><SelectItem value="stationed_reception">Stationed Reception</SelectItem><SelectItem value="combination">Combination</SelectItem><SelectItem value="cocktail_coach">Cocktail Coach</SelectItem></SelectContent></Select></div>
                <div className="mt-4"><Label>Room</Label><Select value={cf.formData.room || ''} onValueChange={(v) => cf.setFormData({ ...cf.formData, room: v })}><SelectTrigger data-testid="room-select"><SelectValue placeholder="Select room" /></SelectTrigger><SelectContent><SelectItem value="pub">Pub</SelectItem><SelectItem value="banquet_hall">Banquet Hall</SelectItem><SelectItem value="lower_patio">Lower Patio</SelectItem><SelectItem value="upper_patio">Upper Patio</SelectItem><SelectItem value="ceremony_space">Ceremony Space</SelectItem><SelectItem value="catering_pickup">Catering Pick Up</SelectItem><SelectItem value="catering_dropoff">Catering Drop Off</SelectItem><SelectItem value="offsite">Offsite</SelectItem></SelectContent></Select></div>
              </div>

              <EventTimingSection formData={cf.formData} setFormData={cf.setFormData} isExpanded={cf.showTimingSection} onToggle={() => cf.setShowTimingSection(!cf.showTimingSection)} />
              <SetupRequirementsSection formData={cf.formData} setFormData={cf.setFormData} isExpanded={cf.showSetupSection} onToggle={() => cf.setShowSetupSection(!cf.showSetupSection)} />

              {/* Delivery Contact - dropoff only */}
              {cf.formData.eventType === 'dropoff' && (
                <div className="mb-4 bg-blue-50/50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2 flex items-center gap-2">
                    <MapPin size={14} /> Delivery Contact
                    <span className="text-[10px] text-gray-500 font-normal">(prints on driver's BEO)</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Contact Name at Delivery Address</Label>
                      <Input
                        data-testid="delivery-contact-name-input"
                        value={cf.formData.deliveryContactName || ''}
                        onChange={(e) => cf.setFormData({ ...cf.formData, deliveryContactName: e.target.value })}
                        placeholder="e.g., Jane Smith (Event Coordinator)"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Phone Number</Label>
                      <Input
                        data-testid="delivery-contact-phone-input"
                        value={cf.formData.deliveryContactPhone || ''}
                        onChange={(e) => cf.setFormData({ ...cf.formData, deliveryContactPhone: e.target.value })}
                        placeholder="519-555-1234"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Selection Grid - for event types with standard menus */}
              {cf.formData.eventType !== 'pickup' && cf.formData.eventType !== 'dropoff' && (
                <MenuSelectionGrid cf={cf} />
              )}

              {/* Custom Menus - per service phase */}
              <div className="border-b pb-4">
                <button type="button" data-testid="custom-menu-toggle" className="flex items-center gap-2 w-full text-left font-semibold text-[#1e3a5f] mb-2"
                  onClick={() => cf.setShowCustomMenuSection(!cf.showCustomMenuSection)}>
                  <ChevronRight size={16} className={`transition-transform ${cf.showCustomMenuSection ? 'rotate-90' : ''}`} />
                  Custom Menus
                  {(cf.formData.customMenu || cf.formData.customMenuBreakfast || cf.formData.customMenuLunch || cf.formData.customMenuCocktail || cf.formData.customMenuDinner || cf.formData.customMenuDessert || cf.formData.customMenuLateNight || cf.formData.customMenuBar || cf.formData.customMenuBeverage || cf.formData.customMenuOnTheTurn || (cf.formData.customMenuBreakfastTiers || []).length > 0 || (cf.formData.customMenuLunchTiers || []).length > 0 || (cf.formData.customMenuCocktailTiers || []).length > 0 || (cf.formData.customMenuDinnerTiers || []).length > 0 || (cf.formData.customMenuDessertTiers || []).length > 0 || (cf.formData.customMenuLateNightTiers || []).length > 0 || (cf.formData.customMenuBarTiers || []).length > 0 || (cf.formData.customMenuBeverageTiers || []).length > 0 || (cf.formData.customMenuOnTheTurnTiers || []).length > 0) && <span className="text-xs font-normal text-green-600 ml-2">has notes</span>}
                </button>
                {cf.showCustomMenuSection && (
                  <div className="space-y-3 pl-1">
                    {/* Pickup/Dropoff general custom menu */}
                    {(cf.formData.eventType === 'pickup' || cf.formData.eventType === 'dropoff') && (
                      <div>
                        <Label className="text-xs text-[#1e3a5f] font-semibold">Catering Menu</Label>
                        <Textarea data-testid="custom-menu-input" value={cf.formData.customMenu}
                          onChange={(e) => cf.setFormData({ ...cf.formData, customMenu: e.target.value })}
                          placeholder="Enter menu items, one per line..." rows={5} className="font-mono text-sm mt-1" />
                      </div>
                    )}
                    {/* Phase-specific custom menus (Breakfast / Lunch / Cocktail / Dinner / Dessert / Late Night / Bar / Beverage / On the Turn) — available for every event type, including catering pickup / dropoff */}
                    {true && (
                      <>
                        <CustomMenuPhase
                          label="Breakfast"
                          textKey="customMenuBreakfast" priceKey="customMenuBreakfastPrice" tiersKey="customMenuBreakfastTiers"
                          textTestId="custom-menu-breakfast" priceTestId="custom-menu-breakfast-price" tierTestIdPrefix="custom-menu-breakfast-tier"
                          placeholder="Custom breakfast items...&#10;e.g. Eggs Benedict, Pancakes, Bacon & Sausage" cf={cf} />
                        <CustomMenuPhase
                          label="Lunch"
                          textKey="customMenuLunch" priceKey="customMenuLunchPrice" tiersKey="customMenuLunchTiers"
                          textTestId="custom-menu-lunch" priceTestId="custom-menu-lunch-price" tierTestIdPrefix="custom-menu-lunch-tier"
                          placeholder="Custom lunch items...&#10;e.g. Caesar Salad, Sandwich Platter, Soup of the Day" cf={cf} />
                        <CustomMenuPhase
                          label="Cocktail Hour / Hors d'Oeuvres"
                          textKey="customMenuCocktail" priceKey="customMenuCocktailPrice" tiersKey="customMenuCocktailTiers"
                          textTestId="custom-menu-cocktail" priceTestId="custom-menu-cocktail-price" tierTestIdPrefix="custom-menu-cocktail-tier"
                          placeholder="Custom cocktail hour items...&#10;e.g. Bruschetta, Chicken Satay, Caprese Skewers" cf={cf} />
                        <CustomMenuPhase
                          label="Dinner Service"
                          textKey="customMenuDinner" priceKey="customMenuDinnerPrice" tiersKey="customMenuDinnerTiers"
                          textTestId="custom-menu-dinner" priceTestId="custom-menu-dinner-price" tierTestIdPrefix="custom-menu-dinner-tier"
                          placeholder="Custom dinner items...&#10;e.g. Roast Beef au Jus, Garlic Mashed Potatoes, Seasonal Vegetables" cf={cf} />
                        <CustomMenuPhase
                          label="Dessert"
                          textKey="customMenuDessert" priceKey="customMenuDessertPrice" tiersKey="customMenuDessertTiers"
                          textTestId="custom-menu-dessert" priceTestId="custom-menu-dessert-price" tierTestIdPrefix="custom-menu-dessert-tier"
                          placeholder="Custom dessert items...&#10;e.g. Assorted Dessert Squares, Fresh Fruit Platter, Chocolate Fondue" cf={cf} />
                        {/* Coffee / Tea — sits next to Dessert so kitchen sees them together on the BEO */}
                        <div className="p-3 border-2 border-gray-200 rounded-lg bg-white">
                          <label className="flex items-center gap-2 mb-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!cf.formData.coffeeService}
                              onChange={(e) => cf.setFormData({ ...cf.formData, coffeeService: e.target.checked })}
                              data-testid="coffee-service-checkbox"
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-semibold text-[#1e3a5f]">Coffee / Tea Service</span>
                          </label>
                          {cf.formData.coffeeService && (
                            <textarea
                              value={cf.formData.coffeeNotes || ''}
                              onChange={(e) => cf.setFormData({ ...cf.formData, coffeeNotes: e.target.value })}
                              placeholder="What they need — e.g. 3 decanters coffee, 2 carafes hot water, cream & sugar setup"
                              className="w-full text-sm p-2 border-2 border-gray-200 rounded focus:border-[#1e3a5f] focus:outline-none"
                              rows={3}
                              data-testid="coffee-notes-input"
                            />
                          )}
                        </div>
                        <CustomMenuPhase
                          label="Late Night"
                          textKey="customMenuLateNight" priceKey="customMenuLateNightPrice" tiersKey="customMenuLateNightTiers"
                          textTestId="custom-menu-late-night" priceTestId="custom-menu-late-night-price" tierTestIdPrefix="custom-menu-late-night-tier"
                          placeholder="Custom late night items...&#10;e.g. Poutine Bar, Slider Station, Dessert Table" cf={cf} />
                        <CustomMenuPhase
                          label="Bar"
                          textKey="customMenuBar" priceKey="customMenuBarPrice" tiersKey="customMenuBarTiers"
                          textTestId="custom-menu-bar" priceTestId="custom-menu-bar-price" tierTestIdPrefix="custom-menu-bar-tier"
                          placeholder="Custom bar package...&#10;e.g. Premium Open Bar, Signature Cocktails, Wine Pairings" cf={cf} />
                        <CustomMenuPhase
                          label="Beverage"
                          textKey="customMenuBeverage" priceKey="customMenuBeveragePrice" tiersKey="customMenuBeverageTiers"
                          textTestId="custom-menu-beverage" priceTestId="custom-menu-beverage-price" tierTestIdPrefix="custom-menu-beverage-tier"
                          placeholder="Custom beverage items...&#10;e.g. Coffee/Tea Service, Soft Drinks, Juice Bar" cf={cf} />
                        <CustomMenuPhase
                          label="On the Turn"
                          textKey="customMenuOnTheTurn" priceKey="customMenuOnTheTurnPrice" tiersKey="customMenuOnTheTurnTiers"
                          textTestId="custom-menu-on-the-turn" priceTestId="custom-menu-on-the-turn-price" tierTestIdPrefix="custom-menu-on-the-turn-tier"
                          placeholder="Custom on-the-turn items (golf tournaments)...&#10;e.g. Turn Dogs, Turn Burgers, Halfway House Snacks" cf={cf} />
                      </>
                    )}
                  </div>
                )}
              </div>

              {cf.showHorsDoeuvresSection && <HorsDoeuvresSection cf={cf} />}
              {cf.showLateNightSection && <LateNightSection cf={cf} />}
              {cf.showBuffetSection && <BuffetBuilderSection cf={cf} />}
              {cf.showPlatedSection && <PlatedDinnerSection cf={cf} />}

              {/* On the Turn - for golf tournaments */}
              {cf.onTheTurnItems?.length > 0 && (
                <div className="border-b pb-4">
                  <button type="button" data-testid="on-the-turn-toggle" className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3" onClick={() => cf.setShowOnTheTurnSection(!cf.showOnTheTurnSection)}>
                    <span className="flex items-center gap-2">On the Turn <span className="text-xs font-normal text-gray-500">(tax included)</span>{cf.selectedOnTheTurnItems.length > 0 && <span className="text-xs text-green-600 ml-1">{cf.selectedOnTheTurnItems.length} selected</span>}</span>
                    <span className="text-sm text-gray-500">{cf.showOnTheTurnSection ? '−' : '+'}</span>
                  </button>
                  {cf.showOnTheTurnSection && (
                    <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                      {cf.onTheTurnItems.map(item => {
                        const selected = cf.selectedOnTheTurnItems.find(s => s.id === item.id);
                        return (
                          <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${selected ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`} data-testid={`ott-item-${item.id}`}>
                            <div className="flex-1 cursor-pointer" onClick={() => cf.toggleOnTheTurnItem(item)}>
                              <span className="font-medium text-sm">{item.name}</span>
                              <span className="text-sm text-[#1e3a5f] ml-2">${(item.price_per_person || 0).toFixed(2)} each</span>
                            </div>
                            {selected && (
                              <div className="flex items-center gap-2">
                                <Label className="text-xs text-gray-500">Qty:</Label>
                                <Input type="number" min="0" className="w-20 h-8 text-sm" value={selected.quantity || ''} onChange={(e) => cf.updateOnTheTurnQuantity(item.id, e.target.value)} data-testid={`ott-qty-${item.id}`} />
                                <span className="text-xs text-gray-500 w-16">${((item.price_per_person || 0) * (selected.quantity || 0)).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {cf.selectedOnTheTurnItems.length > 0 && (
                        <div className="text-right text-sm font-semibold text-green-700 pt-2 border-t">
                          On the Turn Total: ${cf.selectedOnTheTurnItems.reduce((s, i) => s + (i.price_per_person || 0) * (i.quantity || 0), 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <AddonsSection addonItems={cf.addonItems} selectedAddons={cf.selectedAddons} onToggleAddon={cf.toggleAddon} guestCount={parseInt(cf.formData.guestCount) || 0} isExpanded={cf.showAddonsSection} onToggle={() => cf.setShowAddonsSection(!cf.showAddonsSection)} />
              <CustomStaffAddons cf={cf} />
              <DiscountSection cf={cf} />
              <BarServiceSection formData={cf.formData} setFormData={cf.setFormData} selectedMenuIds={cf.selectedMenuIds} toggleMenuSelection={cf.toggleMenuSelection} getMenusByCategory={cf.getMenusByCategory} sortMenuItems={cf.sortMenuItems} barItems={cf.barItems} selectedBarItems={cf.selectedBarItems} toggleBarItem={cf.toggleBarItem} isExpanded={cf.showBarSection} onToggle={() => cf.setShowBarSection(!cf.showBarSection)} />
              {cf.formData.eventType === 'cocktail_coach' && <TravelFeeSection isExpanded={cf.showTravelFeeSection} onToggle={() => cf.setShowTravelFeeSection(!cf.showTravelFeeSection)} location={cf.formData.location} travelFee={cf.travelFee} travelDistanceKm={cf.travelDistanceKm} travelChargeableKm={cf.travelChargeableKm} onTravelFeeCalculated={({ travelFee: fee, travelDistanceKm: dist, travelChargeableKm: charge }) => { cf.setTravelFee(fee); cf.setTravelDistanceKm(dist); cf.setTravelChargeableKm(charge); }} />}
              <TablePlannerSection guestCount={parseInt(cf.formData.guestCount) || 0} rentals={cf.rentals} selectedRentals={cf.selectedRentals} onApplyCalculations={(items) => { const updated = [...cf.selectedRentals]; items.forEach(item => { const idx = updated.findIndex(r => r.id === item.id); if (idx >= 0) updated[idx] = { ...updated[idx], quantity: item.quantity }; else updated.push({ id: item.id, name: item.name, price: item.price_per_unit || item.price || 0, quantity: item.quantity, category: item.category }); }); cf.setSelectedRentals(updated); cf.setShowRentalsSection(true); cf.toast({ title: `Added ${items.length} items!` }); }} isExpanded={cf.showTablePlannerSection} onToggle={() => cf.setShowTablePlannerSection(!cf.showTablePlannerSection)} />
              <RentalsSection rentals={cf.rentals} selectedRentals={cf.selectedRentals} onToggleRental={cf.toggleRentalSelection} onUpdateQuantity={cf.updateRentalQuantity} onUpdateColor={cf.updateRentalColor} isExpanded={cf.showRentalsSection} onToggle={() => cf.setShowRentalsSection(!cf.showRentalsSection)} calculateTotal={cf.calculateRentalsTotal} />
              <EquipmentSection equipment={cf.equipment} selectedEquipment={cf.selectedEquipment} onToggleEquipment={cf.toggleEquipmentSelection} onUpdateQuantity={cf.updateEquipmentQuantity} isExpanded={cf.showEquipmentSection} onToggle={() => cf.setShowEquipmentSection(!cf.showEquipmentSection)} />

              <div><Label htmlFor="specialRequests">Special Requests</Label><Textarea id="specialRequests" data-testid="special-requests-input" value={cf.formData.specialRequests} onChange={(e) => cf.setFormData({ ...cf.formData, specialRequests: e.target.value })} rows={3} /></div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Label htmlFor="kitchenNotes" className="text-amber-800 font-semibold text-sm flex items-center gap-2">Kitchen Notes <span className="text-[10px] font-normal text-amber-600">(BOH only — prints on BEO, not client-facing)</span></Label>
                <Textarea id="kitchenNotes" data-testid="kitchen-notes-input" value={cf.formData.kitchenNotes} onChange={(e) => cf.setFormData({ ...cf.formData, kitchenNotes: e.target.value })} rows={2} className="mt-1 border-amber-200" placeholder="e.g., Bride is celiac — separate prep area. Nut allergy table 5." />
              </div>

              {/* Staff Assignment */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-semibold text-[#1e3a5f] text-sm">Staff Assignment (BEO)</Label>
                  <Button type="button" variant="outline" size="sm" className="text-xs h-7" data-testid="add-staff-btn"
                    onClick={() => cf.setFormData({ ...cf.formData, assignedStaff: [...(cf.formData.assignedStaff || []), { name: '', role: 'Server' }] })}>
                    <Plus size={12} className="mr-1" /> Add Staff
                  </Button>
                </div>
                {(cf.formData.assignedStaff || []).map((staff, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <Input className="flex-1 h-8 text-sm" placeholder="Staff name" value={staff.name} data-testid={`staff-name-${idx}`}
                      onChange={(e) => { const arr = [...cf.formData.assignedStaff]; arr[idx] = { ...arr[idx], name: e.target.value }; cf.setFormData({ ...cf.formData, assignedStaff: arr }); }} />
                    <Select value={staff.role} onValueChange={(v) => { const arr = [...cf.formData.assignedStaff]; arr[idx] = { ...arr[idx], role: v }; cf.setFormData({ ...cf.formData, assignedStaff: arr }); }}>
                      <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Server">Server</SelectItem>
                        <SelectItem value="Bartender">Bartender</SelectItem>
                        <SelectItem value="Chef">Chef</SelectItem>
                        <SelectItem value="Cook">Cook</SelectItem>
                        <SelectItem value="Lead">Lead</SelectItem>
                        <SelectItem value="Setup">Setup</SelectItem>
                        <SelectItem value="Driver">Driver</SelectItem>
                        <SelectItem value="Manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                      onClick={() => { const arr = [...cf.formData.assignedStaff]; arr.splice(idx, 1); cf.setFormData({ ...cf.formData, assignedStaff: arr }); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>

              <DietarySection formData={cf.formData} setFormData={cf.setFormData} />

              {/* Gratuity */}
              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <input type="checkbox" id="includeGratuity" data-testid="include-gratuity-checkbox" checked={cf.formData.includeGratuity} onChange={(e) => cf.setFormData({ ...cf.formData, includeGratuity: e.target.checked })} className="w-4 h-4" />
                  <Label htmlFor="includeGratuity" className="cursor-pointer">Include Gratuity</Label>
                </div>
                {cf.formData.includeGratuity && (
                  <div className="ml-6"><Label>Gratuity Rate</Label><div className="flex gap-4 mt-2">
                    <label className="flex items-center space-x-2"><input type="radio" name="gratuityRate" value="0.15" checked={cf.formData.gratuityRate === '0.15'} onChange={(e) => cf.setFormData({ ...cf.formData, gratuityRate: e.target.value })} data-testid="gratuity-rate-15" /><span>15%</span></label>
                    <label className="flex items-center space-x-2"><input type="radio" name="gratuityRate" value="0.18" checked={cf.formData.gratuityRate === '0.18'} onChange={(e) => cf.setFormData({ ...cf.formData, gratuityRate: e.target.value })} data-testid="gratuity-rate-18" /><span>18%</span></label>
                    <label className="flex items-center space-x-2"><input type="radio" name="gratuityRate" value="0.2" checked={cf.formData.gratuityRate === '0.2' || cf.formData.gratuityRate === '0.20'} onChange={(e) => cf.setFormData({ ...cf.formData, gratuityRate: e.target.value })} data-testid="gratuity-rate-20" /><span>20%</span></label>
                  </div></div>
                )}
              </div>

              <CostBreakdownPreview cf={cf} />
              <Button type="submit" data-testid="create-contract-btn" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">{cf.editingContract ? 'Update Contract' : 'Create Contract'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <Tabs value={cf.eventTypeFilter} onValueChange={cf.setEventTypeFilter} className="mb-6">
        <TabsList className="bg-white flex-wrap">
          {(cf.eventTypes?.length > 0
            ? ['all', ...cf.eventTypes.map(t => t.slug)]
            : ['all','onsite','offsite','tournament','cocktail_coach','pickup','dropoff']
          ).map(t => {
            const meta = cf.eventTypes?.find(x => x.slug === t);
            const label = t === 'all'
              ? 'All'
              : (meta?.name) ||
                (t === 'cocktail_coach' ? 'Cocktail Coach'
                 : t === 'pickup' ? 'Pick Up'
                 : t === 'dropoff' ? 'Drop Off'
                 : t === 'tournament' ? 'Tournaments'
                 : t.charAt(0).toUpperCase() + t.slice(1));
            return (
              <TabsTrigger key={t} value={t} className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white text-xs sm:text-sm">
                {label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Search + Sort + Show Past Events */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search by client, event, contract # or location…"
            value={cf.searchTerm}
            onChange={(e) => cf.setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-9 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1e3a5f]"
            data-testid="contracts-search-input"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          {cf.searchTerm && (
            <button
              type="button"
              onClick={() => cf.setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              data-testid="contracts-search-clear"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <select
          value={cf.statusFilter}
          onChange={(e) => cf.setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1e3a5f] bg-white"
          data-testid="contracts-status-filter"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
        </select>
        <select
          value={cf.sortMode}
          onChange={(e) => cf.setSortMode(e.target.value)}
          className="px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1e3a5f] bg-white"
          data-testid="contracts-sort-select"
        >
          <option value="date_asc">Event date (soonest first)</option>
          <option value="client_asc">Client name (A → Z)</option>
          <option value="status">Status (Pending → Confirmed → Completed)</option>
        </select>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          {cf.filteredContracts.length} contract{cf.filteredContracts.length !== 1 ? 's' : ''}
          {!cf.showPastEvents && (() => {
            const today = new Date().toISOString().split('T')[0];
            const hiddenCount = cf.contracts.filter(c => c.status === 'completed' || (c.event_date && c.event_date < today)).length;
            return hiddenCount > 0 ? <span className="ml-1">({hiddenCount} past hidden)</span> : null;
          })()}
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="show-past-events-toggle">
          <span className="text-sm text-gray-600">Show Past Events</span>
          <button
            type="button"
            role="switch"
            aria-checked={cf.showPastEvents}
            onClick={() => cf.setShowPastEvents(!cf.showPastEvents)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${cf.showPastEvents ? 'bg-[#1e3a5f]' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${cf.showPastEvents ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
          </button>
        </label>
      </div>

      {cf.loading ? (
        <div className="flex justify-center items-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {cf.filteredContracts.map((contract) => (
            <ContractCard key={contract.id} contract={contract} eventTypes={cf.eventTypes} onEdit={cf.openEditContract} onPreview={viewContractPreview} onBEO={viewBEO} onMenuPreview={viewMenuPreview} onInvoice={convertContractToInvoice} onClientAccess={cf.generateClientAccess} onRecordPayment={cf.openRecordPayment} onEventBrief={(c) => { cf.setSelectedContractForBrief(c); cf.setShowEventBriefModal(true); }} onDuplicate={cf.handleDuplicateContract} onStatusChange={cf.handleStatusChange} onDelete={cf.handleDeleteContract} onAIEmail={setAiEmailContract} navigate={navigate} />
          ))}
        </div>
      )}
      {!cf.loading && cf.filteredContracts.length === 0 && <div className="text-center py-20"><p className="text-gray-500 text-lg">No contracts found for this category</p></div>}

      <ClientAccessModal isOpen={cf.showAccessCodeModal} accessCode={cf.generatedAccessCode} contract={cf.selectedContractForAccess} portalUrl={window.location.origin} onClose={() => { cf.setShowAccessCodeModal(false); }} onCopy={() => cf.toast({ title: 'Copied!' })} onSendEmail={cf.sendAccessEmail} onRegenerate={cf.generateClientAccess} isSendingEmail={cf.isSendingAccessEmail} emailSent={cf.accessEmailSent} />

      {/* Payment Modal */}
      {cf.showPaymentModal && cf.paymentContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-[#1e3a5f] text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold flex items-center gap-2"><DollarSign size={24} /> Record Payment</h2>
              <p className="text-sm opacity-90 mt-1">{cf.paymentContract.event_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Total:</span><p className="font-bold text-[#1e3a5f]">${cf.paymentContract.total_amount?.toLocaleString()}</p></div>
                <div><span className="text-gray-500">Paid:</span><p className="font-bold text-blue-600">${(cf.paymentContract.total_paid || 0).toLocaleString()}</p></div>
                <div><span className="text-gray-500">Deposit (25%):</span><p className="font-semibold">${(cf.paymentContract.total_amount * 0.25).toLocaleString()}</p></div>
                <div><span className="text-gray-500">Balance:</span><p className="font-semibold text-amber-600">${(cf.paymentContract.total_amount - (cf.paymentContract.total_paid || 0)).toLocaleString()}</p></div>
              </div>
              <div><Label>Payment Type</Label><select className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white mt-1" value={cf.paymentForm.payment_type} onChange={(e) => { const t = e.target.value; const dep = cf.paymentContract.total_amount * 0.25; const bal = cf.paymentContract.total_amount - (cf.paymentContract.total_paid || 0); cf.setPaymentForm(p => ({ ...p, payment_type: t, amount: t === 'deposit' ? dep.toFixed(2) : t === 'balance' ? bal.toFixed(2) : '' })); }}><option value="deposit">Deposit</option><option value="balance">Balance Payment</option><option value="partial">Partial Payment</option><option value="full">Full Payment</option></select></div>
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" min="0" value={cf.paymentForm.amount} onChange={(e) => cf.setPaymentForm(p => ({ ...p, amount: e.target.value }))} className="mt-1" /></div>
              <div><Label>Payment Method</Label><select className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white mt-1" value={cf.paymentForm.payment_method} onChange={(e) => cf.setPaymentForm(p => ({ ...p, payment_method: e.target.value }))}><option value="cash">Cash</option><option value="check">Check</option><option value="etransfer">E-Transfer</option><option value="credit_card">Credit Card</option><option value="other">Other</option></select></div>
              <div><Label>Reference # (optional)</Label><Input type="text" placeholder="Check #, transaction ID..." value={cf.paymentForm.reference_number} onChange={(e) => cf.setPaymentForm(p => ({ ...p, reference_number: e.target.value }))} className="mt-1" /></div>
              <div><Label>Notes (optional)</Label><textarea className="w-full px-3 py-2 rounded-md border border-gray-300 mt-1" rows={2} value={cf.paymentForm.notes} onChange={(e) => cf.setPaymentForm(p => ({ ...p, notes: e.target.value }))} /></div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => { cf.setShowPaymentModal(false); cf.setPaymentContract(null); }} disabled={cf.savingPayment}>Cancel</Button>
                <Button className="flex-1 bg-[#1e3a5f]" onClick={cf.handleSavePayment} disabled={cf.savingPayment}>{cf.savingPayment ? <><Loader2 className="animate-spin mr-2" size={16} />Recording...</> : <><DollarSign size={16} className="mr-2" />Record Payment</>}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {cf.showEventBriefModal && cf.selectedContractForBrief && (
        <EventCaptainBrief contract={cf.selectedContractForBrief} onClose={() => { cf.setShowEventBriefModal(false); cf.setSelectedContractForBrief(null); }} onSave={() => cf.toast({ title: 'Event brief submitted!' })} />
      )}

      {aiEmailContract && (
        <AIAssistModal mode="email" contract={aiEmailContract} onClose={() => setAiEmailContract(null)} />
      )}
    </div>
  );
};

/* Reusable custom-menu phase block: per-person price fallback + multi-tier pricing */
const CustomMenuPhase = ({ label, textKey, priceKey, tiersKey, textTestId, priceTestId, tierTestIdPrefix, placeholder, cf }) => {
  const guests = parseInt(cf.formData.guestCount) || 0;
  const tiers = cf.formData[tiersKey] || [];
  const validTiers = tiers.filter(t => (parseInt(t.quantity) || 0) > 0 && (parseFloat(t.price_per_person) || 0) > 0);
  const tierTotal = validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
  const fallbackPrice = parseFloat(cf.formData[priceKey]) || 0;
  const fallbackTotal = fallbackPrice * guests;
  const usingTiers = validTiers.length > 0;
  const total = usingTiers ? tierTotal : fallbackTotal;
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label className="text-xs text-[#1e3a5f] font-semibold">{label}</Label>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-500">$</span>
          <input
            type="number" min="0" step="0.01"
            data-testid={priceTestId}
            value={cf.formData[priceKey]}
            onChange={(e) => cf.setFormData({ ...cf.formData, [priceKey]: e.target.value })}
            placeholder="0.00"
            disabled={usingTiers}
            className={`w-20 px-2 py-1 text-xs border rounded text-right ${usingTiers ? 'bg-gray-100 text-gray-400' : ''}`}
            title={usingTiers ? 'Disabled — using tier pricing below' : 'Flat per-person price (used when no tiers)'}
          />
          <span className="text-gray-500 whitespace-nowrap">/ person</span>
          {total > 0 && (
            <span className="ml-2 text-[#1e3a5f] font-semibold whitespace-nowrap">= ${total.toFixed(2)}</span>
          )}
        </div>
      </div>
      <Textarea
        data-testid={textTestId}
        value={cf.formData[textKey]}
        onChange={(e) => cf.setFormData({ ...cf.formData, [textKey]: e.target.value })}
        placeholder={placeholder} rows={3} className="font-mono text-sm mt-1"
      />
      <CustomMenuTiers
        tiers={tiers}
        onChange={(next) => cf.setFormData({ ...cf.formData, [tiersKey]: next })}
        testIdPrefix={tierTestIdPrefix}
      />
    </div>
  );
};


/* ── Inline Sub-components ── */

const SelectableItem = ({ item, isSelected, onToggle, testIdPrefix = 'item' }) => (
  <div onClick={() => onToggle(item)} data-testid={`${testIdPrefix}-${item.id}`}
    className={`p-2 rounded-lg border cursor-pointer transition-all text-sm flex items-center gap-2 ${isSelected ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white' : 'border-gray-200 bg-white hover:border-[#1e3a5f]'}`}>
    {isSelected && <Check size={14} />}
    <span>{item.name}</span>
    {item.has_upcharge && <span className="text-xs opacity-70">*</span>}
  </div>
);

const SelectableItemWithDesc = ({ item, isSelected, onToggle, testIdPrefix = 'item' }) => (
  <div onClick={() => onToggle(item)} data-testid={`${testIdPrefix}-${item.id}`}
    className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white' : 'border-gray-200 bg-white hover:border-[#1e3a5f]'}`}>
    <div className="flex items-center gap-2">{isSelected && <Check size={14} />}<span className="font-medium">{item.name}</span></div>
    {item.description && <p className={`text-xs mt-1 ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>{item.description}</p>}
  </div>
);

const MenuSelectionGrid = ({ cf }) => (
  <div className="border-b pb-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-3">Select Menus ({cf.selectedMenuIds.length} selected)</h3>
    <div className="space-y-4 max-h-80 overflow-y-auto border rounded-lg p-3">
      {Object.entries(cf.getMenusByCategory()).filter(([cat]) => cat !== 'bar_package').map(([category, categoryMenus]) => (
        <div key={category}>
          <h4 className="font-medium text-[#8b7355] mb-2 sticky top-0 bg-white py-1">{cf.formatCategoryName(category)}</h4>
          <div className="space-y-2">
            {categoryMenus.map((menu) => {
              const isSelected = cf.selectedMenuIds.includes(menu.id);
              const override = (cf.formData.menuPriceOverrides || {})[menu.id];
              const hasOverride = override !== undefined && override !== '' && override !== null;
              const needsPrice = !menu.price_per_person && !menu.package_price;
              return (
                <div key={menu.id} className={`rounded-lg transition-all ${isSelected ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  <div onClick={() => cf.toggleMenuSelection(menu.id)} data-testid={`menu-option-${menu.id}`} className="p-3 cursor-pointer">
                    <div className="flex justify-between items-center">
                      <div><p className="font-semibold">{menu.name}</p>{menu.description && <p className={`text-xs ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>{menu.description}</p>}</div>
                      {(menu.price_per_person || menu.package_price) ? (
                        <p className={`font-bold ${hasOverride ? 'line-through opacity-60' : ''}`}>${menu.package_price || menu.price_per_person}{menu.package_price ? ' flat fee' : '/person'}</p>
                      ) : (
                        <p className={`text-xs italic ${isSelected ? 'text-amber-300' : 'text-amber-700'}`}>Price varies — enter below</p>
                      )}
                    </div>
                  </div>
                  {isSelected && !menu.package_price && (
                    <div className="px-3 pb-3" onClick={(e) => e.stopPropagation()}>
                      <div className="bg-white/10 rounded-lg p-2 mt-1 flex items-center gap-2">
                        <label className="text-xs text-gray-200 whitespace-nowrap">
                          {needsPrice ? 'Set price per person:' : 'Override price/person (optional):'}
                        </label>
                        <div className="flex items-center gap-1 flex-1">
                          <span className="text-sm text-gray-200">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={needsPrice ? 'e.g. 18.00' : `${menu.price_per_person?.toFixed(2) || '0.00'}`}
                            value={override ?? ''}
                            onChange={(e) => cf.updateMenuPriceOverride(menu.id, e.target.value)}
                            data-testid={`menu-price-override-${menu.id}`}
                            className="w-28 px-2 py-1 text-sm border rounded text-gray-900 bg-white"
                          />
                          {hasOverride && (
                            <button
                              type="button"
                              onClick={() => cf.updateMenuPriceOverride(menu.id, '')}
                              data-testid={`menu-price-override-clear-${menu.id}`}
                              className="ml-1 text-xs text-amber-300 hover:text-amber-200 underline"
                            >clear</button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSelected && menu.items?.length > 0 && menu.category !== "Hors d'Oeuvres" && menu.category !== "Late Night" && !menu.requires_item_selection && (
                    <div className="px-3 pb-3"><div className="bg-white/10 rounded-lg p-2 mt-1"><p className="text-xs font-medium mb-2 text-gray-200">Items Included:</p><div className="grid grid-cols-2 gap-1">{cf.sortMenuItems(menu.items).map((item) => (<div key={item.id} className="text-xs"><span className="text-white">&#x2022; {item.name}</span></div>))}</div></div></div>
                  )}
                  {isSelected && (menu.category === "Hors d'Oeuvres" || menu.category === "Late Night" || menu.requires_item_selection) && (
                    <div className="px-3 pb-3"><div className="bg-white/10 rounded-lg p-2 mt-1 flex items-center gap-2"><AlertCircle size={14} className="text-amber-300" /><p className="text-xs text-gray-200">Select individual items below</p></div></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {Object.keys(cf.getMenusByCategory()).length === 0 && <p className="text-gray-500 text-center py-4">No menus available for this event type</p>}
    </div>
  </div>
);

const HorsDoeuvresSection = ({ cf }) => {
  const required = cf.getRequiredHDItemCount();
  const hotCount = cf.selectedHorsDoeuvresItems.filter(i => i.sub_category === 'hot').length;
  const coldCount = cf.selectedHorsDoeuvresItems.filter(i => i.sub_category === 'cold').length;
  return (
    <div className="border-b pb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#1e3a5f]">Select Hors d&apos;Oeuvres ({cf.selectedHorsDoeuvresItems.length} selected)</h3>
        {required.total === 6 && <span className={`text-sm ${hotCount === 3 && coldCount === 3 ? 'text-green-600' : 'text-amber-600'}`}>{hotCount}/3 Hot, {coldCount}/3 Cold</span>}
        {required.total === 4 && <span className={`text-sm ${cf.selectedHorsDoeuvresItems.length === 4 ? 'text-green-600' : 'text-amber-600'}`}>Select any 4 items</span>}
      </div>
      <div className="bg-gray-50 p-4 rounded-lg space-y-4 max-h-96 overflow-y-auto">
        {['cold', 'hot', 'favourite'].map(sub => {
          const items = cf.horsDoeuvresItems.filter(i => i.sub_category === sub);
          if (items.length === 0) return null;
          const labels = { cold: 'Cold Hors d\'Oeuvres', hot: 'Hot Hors d\'Oeuvres', favourite: 'Willow Ridge Favourites' };
          return (
            <div key={sub}>
              <h4 className="font-medium text-[#8b7355] mb-2">{labels[sub]} ({cf.selectedHorsDoeuvresItems.filter(i => i.sub_category === sub).length} selected)</h4>
              <div className="grid grid-cols-2 gap-2">{items.map(item => <SelectableItem key={item.id} item={item} isSelected={!!cf.selectedHorsDoeuvresItems.find(i => i.id === item.id)} onToggle={cf.toggleHorsDoeuvresItem} testIdPrefix="hd-item" />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LateNightSection = ({ cf }) => (
  <div className="border-b pb-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-3">Select Late Night Options ({cf.selectedLateNightItems.length} selected)</h3>
    <div className="bg-gray-50 p-4 rounded-lg space-y-2 max-h-64 overflow-y-auto">
      {cf.lateNightItems.map(item => <SelectableItemWithDesc key={item.id} item={item} isSelected={!!cf.selectedLateNightItems.find(i => i.id === item.id)} onToggle={cf.toggleLateNightItem} testIdPrefix="ln-item" />)}
    </div>
  </div>
);

const BuffetBuilderSection = ({ cf }) => {
  const categories = [{ key: 'salad', label: 'Salads (Choose 2)' }, { key: 'starch', label: 'Starches (Choose 2)' }, { key: 'vegetable', label: 'Vegetables (Choose 1)' }, { key: 'main', label: 'Mains' }, { key: 'dessert', label: 'Dessert (Choose 1)' }];
  return (
    <div className="border-b pb-4">
      <h3 className="font-semibold text-[#1e3a5f] mb-3">Build Your Buffet ({cf.selectedBuffetItems.length} items)</h3>
      <div className="bg-gray-50 p-4 rounded-lg space-y-4 max-h-96 overflow-y-auto">
        {categories.map(cat => (
          <div key={cat.key}>
            <h4 className="font-medium text-[#8b7355] mb-2">{cat.label}</h4>
            <div className="grid grid-cols-2 gap-2">{cf.buffetItems.filter(i => i.sub_category === cat.key).map(item => <SelectableItemWithDesc key={item.id} item={item} isSelected={!!cf.selectedBuffetItems.find(i => i.id === item.id)} onToggle={cf.toggleBuffetItem} testIdPrefix="buffet-item" />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PlatedDinnerSection = ({ cf }) => (
  <div className="border-b pb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-[#1e3a5f]">{cf.platedCourses} Course Plated Dinner</h3>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Courses:</span>
          <div className="flex border rounded-lg overflow-hidden">
            {[3, 4].map(n => <button key={n} type="button" onClick={() => { cf.setPlatedCourses(n); if (n === 3) cf.selectPlatedAppetizer(cf.selectedPlatedAppetizer); }} className={`px-3 py-1 text-sm font-medium ${cf.platedCourses === n ? 'bg-[#1e3a5f] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>{n}</button>)}
          </div>
        </div>
        <div className="text-xs text-gray-600 space-x-2">
          {cf.platedCourses === 4 && <span className={cf.selectedPlatedAppetizer ? 'text-green-600' : 'text-amber-600'}>{cf.selectedPlatedAppetizer ? '✓' : '○'} App</span>}
          <span className={cf.selectedPlatedSalad ? 'text-green-600' : 'text-amber-600'}>{cf.selectedPlatedSalad ? '✓' : '○'} Salad</span>
          <span className={cf.selectedPlatedMains.length === 3 ? 'text-green-600' : 'text-amber-600'}>{cf.selectedPlatedMains.length}/3 Mains</span>
          <span className={cf.selectedPlatedDessert ? 'text-green-600' : 'text-amber-600'}>{cf.selectedPlatedDessert ? '✓' : '○'} Dessert</span>
        </div>
      </div>
    </div>
    <div className="bg-gray-50 p-4 rounded-lg space-y-4 max-h-[500px] overflow-y-auto">
      {cf.platedCourses === 4 && (
        <div><h4 className="font-medium text-[#8b7355] mb-2">Select 1 Appetizer</h4><div className="grid grid-cols-2 gap-2">{cf.platedItems.filter(i => i.sub_category === 'appetizer').map(item => <SelectableItemWithDesc key={item.id} item={item} isSelected={cf.selectedPlatedAppetizer?.id === item.id} onToggle={cf.selectPlatedAppetizer} />)}</div></div>
      )}
      <div><h4 className="font-medium text-[#8b7355] mb-2">Select 1 Salad</h4><div className="grid grid-cols-2 gap-2">{cf.platedItems.filter(i => i.sub_category === 'salad').map(item => <SelectableItemWithDesc key={item.id} item={item} isSelected={cf.selectedPlatedSalad?.id === item.id} onToggle={cf.selectPlatedSalad} />)}</div></div>
      <div>
        <h4 className="font-medium text-[#8b7355] mb-2">Select 3 Main Courses (Guests choose one)</h4>
        <div className="grid grid-cols-1 gap-2">
          {cf.platedItems.filter(i => i.sub_category === 'main').map(item => {
            const sel = cf.selectedPlatedMains.find(m => m.item.id === item.id);
            const canSelect = cf.selectedPlatedMains.length < 3 || !!sel;
            return (
              <div key={item.id} className={`p-3 rounded-lg border transition-all ${sel ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' : canSelect ? 'border-gray-200 bg-white hover:border-[#1e3a5f] cursor-pointer' : 'border-gray-100 bg-gray-50 opacity-50'}`}>
                <div className="flex items-center justify-between">
                  <div className={canSelect ? 'cursor-pointer flex-1' : 'flex-1'} onClick={() => canSelect && cf.togglePlatedMain(item)}>
                    <div className="flex items-center gap-2">{sel && <Check size={14} className="text-[#1e3a5f]" />}<span className="font-medium text-sm">{item.name}</span><span className="text-sm font-bold text-[#c9a227]">${item.price_per_person?.toFixed(2)}/person</span></div>
                    {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                  </div>
                  {sel && <div className="flex items-center gap-2 ml-4"><span className="text-xs text-gray-600">Qty:</span><input type="number" min="0" max={parseInt(cf.formData.guestCount) || 999} value={sel.quantity} onChange={(e) => cf.updatePlatedMainQuantity(item.id, parseInt(e.target.value) || 0)} className="w-20 px-2 py-1 text-sm border rounded text-center" onClick={(e) => e.stopPropagation()} />{sel.quantity > 0 && <span className="text-xs text-gray-600">= ${(sel.quantity * (item.price_per_person || 0)).toFixed(2)}</span>}</div>}
                </div>
              </div>
            );
          })}
        </div>
        {cf.selectedPlatedMains.length === 3 && (
          <div className={`mt-3 p-3 rounded-lg ${cf.getPlatedMainsTotal() === parseInt(cf.formData.guestCount || 0) ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex justify-between items-center"><span className="text-sm font-medium">Total Guest Orders:</span><span className={`font-bold ${cf.getPlatedMainsTotal() === parseInt(cf.formData.guestCount || 0) ? 'text-green-600' : 'text-amber-600'}`}>{cf.getPlatedMainsTotal()} / {cf.formData.guestCount || 0} guests</span></div>
            {cf.getPlatedCost() > 0 && <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200"><span className="text-sm font-medium">Plated Dinner Total:</span><span className="font-bold text-[#1e3a5f]">${cf.getPlatedCost().toFixed(2)}</span></div>}
          </div>
        )}
      </div>
      <div><h4 className="font-medium text-[#8b7355] mb-2">Select 1 Dessert</h4><div className="grid grid-cols-2 gap-2">{cf.platedItems.filter(i => i.sub_category === 'dessert').map(item => <SelectableItemWithDesc key={item.id} item={item} isSelected={cf.selectedPlatedDessert?.id === item.id} onToggle={cf.selectPlatedDessert} />)}</div></div>
    </div>
  </div>
);

const CustomStaffAddons = ({ cf }) => {
  const [mode, setMode] = React.useState('hourly'); // 'hourly' | 'per_person' | 'per_item' | 'flat'
  const [templates, setTemplates] = React.useState([]);
  const guestCount = parseInt(cf.formData.guestCount) || 0;

  React.useEffect(() => {
    let cancelled = false;
    customChargeTemplatesAPI.getAll()
      .then(data => { if (!cancelled) setTemplates(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const applyTemplate = (tpl) => {
    if (tpl.pricing_mode === 'per_person' && guestCount <= 0) {
      cf.toast({ title: 'Set guest count first', variant: 'destructive' });
      return;
    }
    let hours = 0;
    let quantity = 0;
    let total = 0;
    if (tpl.pricing_mode === 'hourly') {
      hours = tpl.default_hours || 0;
      total = hours * tpl.rate;
      if (total === 0) total = tpl.rate; // still add with rate even if no default hours
    } else if (tpl.pricing_mode === 'per_person') {
      total = tpl.rate * guestCount;
    } else if (tpl.pricing_mode === 'per_item') {
      quantity = tpl.default_quantity || 1;
      total = quantity * tpl.rate;
    } else {
      total = tpl.rate;
    }
    cf.setFormData(prev => ({
      ...prev,
      customAddons: [...(prev.customAddons || []), {
        id: Date.now(), name: tpl.name, pricing_mode: tpl.pricing_mode, hours, quantity, rate: tpl.rate,
        guest_count_at_add: tpl.pricing_mode === 'per_person' ? guestCount : null,
        total
      }]
    }));
    cf.toast({ title: `Added "${tpl.name}"` });
  };

  const handleAdd = () => {
    const name = document.getElementById('customAddonName').value;
    const rate = parseFloat(document.getElementById('customAddonRate').value) || 0;
    if (!name || rate <= 0) return;

    let hours = 0;
    let quantity = 0;
    let total = 0;
    if (mode === 'hourly') {
      hours = parseFloat(document.getElementById('customAddonHours').value) || 0;
      if (hours <= 0) return;
      total = hours * rate;
    } else if (mode === 'per_person') {
      if (guestCount <= 0) { cf.toast({ title: 'Set guest count first', variant: 'destructive' }); return; }
      total = rate * guestCount;
    } else if (mode === 'per_item') {
      quantity = parseFloat(document.getElementById('customAddonQty').value) || 0;
      if (quantity <= 0) return;
      total = quantity * rate;
    } else {
      total = rate;
    }

    cf.setFormData(prev => ({
      ...prev,
      customAddons: [...(prev.customAddons || []), {
        id: Date.now(), name, pricing_mode: mode, hours, quantity, rate,
        guest_count_at_add: mode === 'per_person' ? guestCount : null,
        total
      }]
    }));
    document.getElementById('customAddonName').value = '';
    document.getElementById('customAddonRate').value = '';
    const hrsEl = document.getElementById('customAddonHours');
    if (hrsEl) hrsEl.value = '';
    const qtyEl = document.getElementById('customAddonQty');
    if (qtyEl) qtyEl.value = '';
  };

  const rateLabel = mode === 'hourly' ? 'Rate/Hr ($)' : mode === 'per_person' ? 'Rate/Person ($)' : mode === 'per_item' ? 'Price/Item ($)' : 'Flat Amount ($)';
  const ratePlaceholder = mode === 'hourly' ? '25.00' : mode === 'per_person' ? '3.50' : mode === 'per_item' ? '4.50' : '50.00';

  return (
    <div className="border-b pb-4">
      <button type="button" onClick={() => cf.setShowCustomAddonsSection(!cf.showCustomAddonsSection)} className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3">
        <span className="flex items-center gap-2">Staff &amp; Custom Charges{cf.formData.customAddons?.length > 0 && <span className="text-sm font-normal text-gray-500">({cf.formData.customAddons.length} items)</span>}</span>
        <span className="text-sm text-gray-500">{cf.showCustomAddonsSection ? '−' : '+'}</span>
      </button>
      {cf.showCustomAddonsSection && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          {templates.length > 0 && (
            <div className="bg-white p-3 rounded border" data-testid="quick-add-templates">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-[#c9a227]" />
                <h4 className="text-sm font-medium">Quick Add</h4>
                <span className="text-xs text-gray-400">(one click adds to contract)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {templates.map(tpl => {
                  const unit = tpl.pricing_mode === 'hourly' ? '/hr' : tpl.pricing_mode === 'per_person' ? '/pp' : ' flat';
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`quick-add-${tpl.id}`}
                      className="px-3 py-1.5 text-xs rounded-full border border-gray-300 bg-white hover:bg-[#1e3a5f] hover:text-white hover:border-[#1e3a5f] transition flex items-center gap-1.5"
                    >
                      <Plus size={12} />
                      <span className="font-medium">{tpl.name}</span>
                      <span className="opacity-70">${tpl.rate.toFixed(2)}{unit}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white p-3 rounded border">
            <h4 className="text-sm font-medium mb-3">Add Staff or Custom Charge</h4>

            {/* Pricing mode toggle */}
            <div className="flex gap-1 mb-3" data-testid="custom-addon-mode-toggle">
              {[
                { k: 'hourly', label: 'Hourly' },
                { k: 'per_person', label: 'Per Person' },
                { k: 'per_item', label: 'Per Item' },
                { k: 'flat', label: 'Flat' },
              ].map(opt => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setMode(opt.k)}
                  data-testid={`custom-addon-mode-${opt.k}`}
                  className={`px-3 py-1.5 text-xs rounded-md border transition ${mode === opt.k ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className={`grid grid-cols-1 gap-3 ${(mode === 'hourly' || mode === 'per_item') ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
              <div><Label className="text-xs">Description</Label><Input id="customAddonName" placeholder="e.g., Extra Server" className="text-sm" data-testid="custom-addon-name" /></div>
              {mode === 'hourly' && (
                <div><Label className="text-xs">Hours</Label><Input id="customAddonHours" type="number" min="0" step="0.5" placeholder="4" className="text-sm" data-testid="custom-addon-hours" /></div>
              )}
              {mode === 'per_item' && (
                <div><Label className="text-xs">Quantity</Label><Input id="customAddonQty" type="number" min="0" step="1" placeholder="50" className="text-sm" data-testid="custom-addon-qty" /></div>
              )}
              <div><Label className="text-xs">{rateLabel}</Label><Input id="customAddonRate" type="number" min="0" step="0.01" placeholder={ratePlaceholder} className="text-sm" data-testid="custom-addon-rate" /></div>
              <div className="flex items-end">
                <Button type="button" onClick={handleAdd} className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid="add-custom-addon-btn">
                  <Plus size={16} className="mr-1" /> Add
                </Button>
              </div>
            </div>
            {mode === 'per_person' && guestCount > 0 && (
              <p className="text-xs text-blue-600 mt-2">Will multiply by {guestCount} guests.</p>
            )}
            {mode === 'per_person' && guestCount === 0 && (
              <p className="text-xs text-red-500 mt-2">Enter Guest Count above before adding a per-person charge.</p>
            )}
            {mode === 'per_item' && (
              <p className="text-xs text-blue-600 mt-2">Total = Quantity × Price/Item (e.g. 50 Champagne Toasts × $4.50 = $225).</p>
            )}
          </div>
          {cf.formData.customAddons?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Added Items:</h4>
              {cf.formData.customAddons.map((addon, idx) => {
                const m = addon.pricing_mode || (addon.hours > 0 ? 'hourly' : 'flat');
                const detail =
                  m === 'hourly' ? `${addon.hours} hrs × $${addon.rate?.toFixed(2)}/hr` :
                  m === 'per_person' ? `${addon.guest_count_at_add || guestCount} × $${addon.rate?.toFixed(2)}/pp` :
                  m === 'per_item' ? `${addon.quantity} × $${addon.rate?.toFixed(2)}/item` :
                  'Flat charge';
                return (
                  <div key={addon.id || idx} className="flex items-center justify-between p-3 bg-white rounded border">
                    <div>
                      <span className="font-medium">{addon.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({detail})</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#1e3a5f]">${addon.total?.toFixed(2)}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => cf.setFormData(prev => ({ ...prev, customAddons: prev.customAddons.filter((_, i) => i !== idx) }))} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                );
              })}
              <div className="text-right pt-2 border-t"><span className="text-sm text-gray-600">Staff/Custom Total: </span><span className="font-bold text-[#1e3a5f]">${cf.formData.customAddons.reduce((s, a) => s + (a.total || 0), 0).toFixed(2)}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DiscountSection = ({ cf }) => {
  const [showSection, setShowSection] = React.useState((cf.formData.discounts || []).length > 0);
  return (
    <div className="border-b pb-4">
      <button type="button" onClick={() => setShowSection(!showSection)} className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3">
        <span className="flex items-center gap-2">Multi-Service Discounts{cf.formData.discounts?.length > 0 && <span className="text-sm font-normal text-green-600">({cf.formData.discounts.length} applied)</span>}</span>
        <span className="text-sm text-gray-500">{showSection ? '−' : '+'}</span>
      </button>
      {showSection && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="bg-white p-3 rounded border">
            <h4 className="text-sm font-medium mb-3">Add Discount</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">Description</Label><Input id="discountName" placeholder="e.g., Multi-event" className="text-sm" data-testid="discount-name" /></div>
              <div><Label className="text-xs">Type</Label><select id="discountType" className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm" data-testid="discount-type"><option value="flat">Flat Amount ($)</option><option value="percentage">Percentage (%)</option></select></div>
              <div><Label className="text-xs">Value</Label><Input id="discountValue" type="number" min="0" step="0.01" placeholder="100.00" className="text-sm" data-testid="discount-value" /></div>
              <div className="flex items-end">
                <Button type="button" data-testid="add-discount-btn" onClick={() => {
                  const name = document.getElementById('discountName').value;
                  const type = document.getElementById('discountType').value;
                  const value = parseFloat(document.getElementById('discountValue').value) || 0;
                  if (name && value > 0) {
                    cf.setFormData(prev => ({ ...prev, discounts: [...(prev.discounts || []), { id: Date.now(), name, type, value }] }));
                    document.getElementById('discountName').value = ''; document.getElementById('discountValue').value = '';
                  }
                }} className="w-full bg-green-700 hover:bg-green-800"><Plus size={16} className="mr-1" /> Add</Button>
              </div>
            </div>
          </div>
          {cf.formData.discounts?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Applied Discounts:</h4>
              {cf.formData.discounts.map((d, idx) => (
                <div key={d.id || idx} className="flex items-center justify-between p-3 bg-white rounded border">
                  <div><span className="font-medium">{d.name}</span><span className="text-sm text-gray-500 ml-2">({d.type === 'percentage' ? `${d.value}%` : `$${d.value.toFixed(2)}`})</span></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-green-700">-{d.type === 'percentage' ? `${d.value}%` : `$${d.value.toFixed(2)}`}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => cf.setFormData(prev => ({ ...prev, discounts: prev.discounts.filter((_, i) => i !== idx) }))} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CostBreakdownPreview = ({ cf }) => {
  const hasItems = cf.selectedMenuIds.length > 0 || cf.selectedRentals.length > 0 || cf.selectedPlatedMains.length > 0 || cf.selectedAddons.length > 0 || (cf.formData.customAddons || []).length > 0 || cf.selectedOnTheTurnItems.length > 0;
  if (!hasItems || !cf.formData.guestCount) return null;
  const t = cf.calculateTotals();
  const customTotal = (cf.formData.customAddons || []).reduce((s, a) => s + (a.total || 0), 0);
  const foodBev = t.menuSubtotal - t.platedSubtotal - t.addonsSubtotal - customTotal;
  return (
    <div className="border-t pt-4 bg-[#faf9f6] rounded-lg p-4" data-testid="cost-breakdown">
      <h3 className="font-semibold text-[#1e3a5f] mb-3 text-sm">Cost Breakdown</h3>
      <div className="space-y-1.5 text-sm">
        {foodBev > 0 && <div className="flex justify-between"><span className="text-gray-600">Food & Beverage ({cf.formData.guestCount} guests)</span><span className="font-medium">${foodBev.toFixed(2)}</span></div>}
        {t.platedSubtotal > 0 && <div className="flex justify-between"><span className="text-gray-600">Plated Dinner</span><span className="font-medium">${t.platedSubtotal.toFixed(2)}</span></div>}
        {t.addonsSubtotal > 0 && <div className="flex justify-between"><span className="text-gray-600">Add-Ons ({cf.selectedAddons.length} items x {cf.formData.guestCount} guests)</span><span className="font-medium">${t.addonsSubtotal.toFixed(2)}</span></div>}
        {customTotal > 0 && <div className="flex justify-between"><span className="text-gray-600">Staff & Custom Charges</span><span className="font-medium">${customTotal.toFixed(2)}</span></div>}
        {t.rentalsSubtotal > 0 && <div className="flex justify-between"><span className="text-gray-600">Rentals</span><span className="font-medium">${t.rentalsSubtotal.toFixed(2)}</span></div>}
        {t.drinkTotal > 0 && <div className="flex justify-between text-purple-700"><span>Bar</span><span className="font-medium">${t.drinkTotal.toFixed(2)}</span></div>}
        {t.onTheTurnSubtotal > 0 && <div className="flex justify-between text-green-700"><span>On the Turn (tax incl.)</span><span className="font-medium">${t.onTheTurnSubtotal.toFixed(2)}</span></div>}
        {t.travelFee > 0 && <div className="flex justify-between text-amber-700"><span>Travel Fee</span><span className="font-medium">${t.travelFee.toFixed(2)}</span></div>}
        {t.discountAmount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span className="font-medium">-${t.discountAmount.toFixed(2)}</span></div>}
        <div className="border-t border-gray-300 pt-1.5 flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">${t.subtotal.toFixed(2)}</span></div>
        {(cf.formData.eventType === 'tournament' || cf.formData.eventType === 'golf_tournament') ? (
          <div className="flex justify-between text-green-700"><span>Tax & Gratuity</span><span className="font-medium text-xs">Included</span></div>
        ) : (
          <>
            <div className="flex justify-between"><span className="text-gray-600">HST (13%)</span><span className="font-medium">${t.taxAmount.toFixed(2)}</span></div>
            {cf.formData.includeGratuity && t.gratuityAmount > 0 && <div className="flex justify-between"><span className="text-gray-600">Gratuity ({(parseFloat(cf.formData.gratuityRate) * 100).toFixed(0)}%)</span><span className="font-medium">${t.gratuityAmount.toFixed(2)}</span></div>}
          </>
        )}
        <div className="border-t-2 border-[#1e3a5f] pt-1.5 flex justify-between text-base"><span className="font-bold text-[#1e3a5f]">Estimated Total</span><span className="font-bold text-[#1e3a5f]">${t.total.toFixed(2)}</span></div>
      </div>
    </div>
  );
};

export default Contracts;
